import { latLngBounds, type LatLngBounds, type LatLngTuple, type Map as LeafletMap } from 'leaflet';
import divisionShapes from './bangladeshDivisions.json';
import {
  MAP_COUNTRY_FIT_PADDING,
  MAP_SINGLE_POINT_ZOOM,
  MAP_VIEW_FLY_DURATION_SECONDS,
} from './map.constants';

export interface DivisionShape {
  name: string;
  centre: LatLngTuple;
  rings: LatLngTuple[][];
}

export const DIVISIONS = divisionShapes as DivisionShape[];

export function findDivisionByName(name: string | null): DivisionShape | null {
  if (!name) return null;
  const normalizedName = name.trim().toLowerCase();
  return DIVISIONS.find((division) => division.name.toLowerCase() === normalizedName) ?? null;
}

export const COUNTRY_BOUNDS = latLngBounds(DIVISIONS.flatMap((division) => division.rings.flat()));

const COUNTRY_SPREAD_SHARE = 0.5;

function isSpreadAcrossCountry(bounds: LatLngBounds): boolean {
  const latitudeShare =
    (bounds.getNorth() - bounds.getSouth()) /
    (COUNTRY_BOUNDS.getNorth() - COUNTRY_BOUNDS.getSouth());
  const longitudeShare =
    (bounds.getEast() - bounds.getWest()) / (COUNTRY_BOUNDS.getEast() - COUNTRY_BOUNDS.getWest());
  return Math.max(latitudeShare, longitudeShare) >= COUNTRY_SPREAD_SHARE;
}

interface MapMoveOptions {
  animate: boolean;
  shouldFly?: boolean;
}

interface FitPointsOptions extends MapMoveOptions {
  padding: [number, number];
}

function moveToBounds(
  map: LeafletMap,
  bounds: LatLngBounds,
  padding: [number, number],
  { animate, shouldFly = false }: MapMoveOptions,
) {
  if (animate && shouldFly) {
    map.flyToBounds(bounds, { padding, duration: MAP_VIEW_FLY_DURATION_SECONDS });
    return;
  }
  map.fitBounds(bounds, { padding, animate });
}

function moveToPoint(
  map: LeafletMap,
  point: LatLngTuple,
  { animate, shouldFly = false }: MapMoveOptions,
) {
  if (animate && shouldFly) {
    map.flyTo(point, MAP_SINGLE_POINT_ZOOM, { duration: MAP_VIEW_FLY_DURATION_SECONDS });
    return;
  }
  map.setView(point, MAP_SINGLE_POINT_ZOOM, { animate });
}

export function tryFitPointsInCountry(
  map: LeafletMap,
  points: LatLngTuple[],
  options: FitPointsOptions,
): boolean {
  const { padding } = options;
  const mapSize = map.getSize();
  if (mapSize.x <= padding[0] * 2 || mapSize.y <= padding[1] * 2) return false;

  const [firstPoint] = points;
  if (!firstPoint) {
    moveToBounds(map, COUNTRY_BOUNDS, MAP_COUNTRY_FIT_PADDING, options);
    return true;
  }

  if (points.length === 1) {
    moveToPoint(map, firstPoint, options);
    return true;
  }

  const pointBounds = latLngBounds(points);
  if (isSpreadAcrossCountry(pointBounds)) {
    moveToBounds(map, COUNTRY_BOUNDS, MAP_COUNTRY_FIT_PADDING, options);
    return true;
  }

  moveToBounds(map, pointBounds, padding, options);
  return true;
}
